import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Key, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    if (!/[!@#$%^&*]/.test(password)) errors.push('One special character (!@#$%^&*)');
    return errors;
  };

  const passwordErrors = validatePassword(newPassword);
  const isValidPassword = passwordErrors.length === 0 && newPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!isValidPassword) {
      toast.error('Please meet all password requirements');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/change-password?employee_id=${user?.id}`,
        { current_password: currentPassword, new_password: newPassword }
      );
      toast.success('Password changed successfully! Please login again.');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-900/80 border border-slate-800 rounded-sm p-8 backdrop-blur-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <Key className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Change Your Password</h1>
            <p className="text-slate-400 text-sm mt-2">
              Your temporary password has expired. Please create a new password to continue.
            </p>
          </div>

          {/* Alert */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-yellow-300">
              For security reasons, you must change your temporary password before accessing the dashboard.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="current" className="text-slate-300">Current/Temporary Password</Label>
              <Input
                id="current"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your temporary password"
                className="bg-slate-800 border-slate-700 text-white h-11"
                data-testid="current-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new" className="text-slate-300">New Password</Label>
              <Input
                id="new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Create a new password"
                className="bg-slate-800 border-slate-700 text-white h-11"
                data-testid="new-password-input"
              />
              
              {/* Password Requirements */}
              <div className="bg-slate-800/50 rounded-sm p-3 mt-2">
                <p className="text-xs text-slate-400 mb-2">Password must contain:</p>
                <ul className="space-y-1">
                  {[
                    { check: newPassword.length >= 8, text: 'At least 8 characters' },
                    { check: /[A-Z]/.test(newPassword), text: 'One uppercase letter' },
                    { check: /[a-z]/.test(newPassword), text: 'One lowercase letter' },
                    { check: /[0-9]/.test(newPassword), text: 'One number' },
                    { check: /[!@#$%^&*]/.test(newPassword), text: 'One special character (!@#$%^&*)' },
                  ].map((req, i) => (
                    <li key={i} className={`text-xs flex items-center gap-2 ${req.check ? 'text-green-400' : 'text-slate-500'}`}>
                      {req.check ? (
                        <CheckCircle2 className="w-3 h-3" />
                      ) : (
                        <div className="w-3 h-3 rounded-full border border-slate-600" />
                      )}
                      {req.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-slate-300">Confirm New Password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                className="bg-slate-800 border-slate-700 text-white h-11"
                data-testid="confirm-password-input"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-400">Passwords do not match</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || !isValidPassword || newPassword !== confirmPassword}
              className="w-full h-11 bg-green-500 hover:bg-green-600 text-slate-900 font-bold shadow-[0_0_15px_rgba(34,197,94,0.3)] disabled:opacity-50"
              data-testid="change-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Changing Password...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
